import React, { useState } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { EscortProfile } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

// <-- Définir les options ici -->
const genderOptions = [
  { value: 'female', label: 'common.gender.female' },
  { value: 'male', label: 'common.gender.male' },
  { value: 'transgender_female', label: 'common.gender.trans_female' },
  { value: 'transgender_male', label: 'common.gender.trans_male' },
  { value: 'non_binary', label: 'common.gender.non_binary' },
  // Ajouter d'autres si nécessaire
];

const orientationOptions = [
  { value: 'heterosexual', label: 'common.orientation.heterosexual' },
  { value: 'homosexual', label: 'common.orientation.homosexual' },
  { value: 'bisexual', label: 'common.orientation.bisexual' },
  { value: 'pansexual', label: 'common.orientation.pansexual' },
  { value: 'asexual', label: 'common.orientation.asexual' },
  // Ajouter d'autres si nécessaire
];

const ethnicityOptions = [
  { value: 'asian', label: 'common.ethnicity.asian' },
  { value: 'black', label: 'common.ethnicity.black' },
  { value: 'caucasian', label: 'common.ethnicity.caucasian' },
  { value: 'hispanic_latino', label: 'common.ethnicity.hispanic_latino' },
  { value: 'middle_eastern', label: 'common.ethnicity.middle_eastern' },
  { value: 'native_american', label: 'common.ethnicity.native_american' },
  { value: 'pacific_islander', label: 'common.ethnicity.pacific_islander' },
  { value: 'mixed', label: 'common.ethnicity.mixed' },
  { value: 'other', label: 'common.ethnicity.other' },
];

const bodyTypeOptions = [
  { value: 'slim', label: 'common.body_type.slim' },
  { value: 'athletic', label: 'common.body_type.athletic' },
  { value: 'average', label: 'common.body_type.average' },
  { value: 'curvy', label: 'common.body_type.curvy' },
  { value: 'bbw', label: 'common.body_type.bbw' }, // Assurez-vous que la clé existe
  { value: 'muscular', label: 'common.body_type.muscular' },
  { value: 'petite', label: 'common.body_type.petite' },
];
// <-- Fin de la définition des options -->

interface BasicInfoTabProps {
  profile: EscortProfile;
  onProfileUpdate: (updatedProfile: EscortProfile) => void;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({ profile, onProfileUpdate }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    displayName: profile.displayName || '',
    age: profile.age || 18,
    bioShort: profile.bio?.short || '',
    bioFull: profile.bio?.full || '',
    city: profile.location?.city || '',
    country: profile.location?.country || '',
    height: profile.attributes?.height || '',
    weight: profile.attributes?.weight || '',
    gender: profile.attributes?.gender || '',
    sexualOrientation: profile.attributes?.sexualOrientation || '',
    ethnicity: profile.attributes?.ethnicity || '',
    nationality: profile.attributes?.nationality || '',
    hairColor: profile.attributes?.hairColor || '',
    eyeColor: profile.attributes?.eyeColor || '',
    bodyType: profile.attributes?.bodyType || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numberValue = value === '' ? '' : parseInt(value, 10);
    setFormData(prev => ({ ...prev, [name]: numberValue }));
  };

  const saveProfileInfo = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('escort_profiles')
        .update({
          display_name: formData.displayName,
          age: formData.age,
          bio_short: formData.bioShort,
          bio_full: formData.bioFull,
          city: formData.city,
          country: formData.country,
          height: formData.height,
          weight: formData.weight,
          gender: formData.gender,
          sexual_orientation: formData.sexualOrientation,
          ethnicity: formData.ethnicity,
          nationality: formData.nationality,
          hair_color: formData.hairColor,
          eye_color: formData.eyeColor,
          body_type: formData.bodyType,
        })
        .eq('id', profile.id);

      if (error) throw error;

      // Update profile in parent component
      const updatedProfile = {
        ...profile,
        displayName: formData.displayName,
        age: Number(formData.age),
        bio: {
          short: formData.bioShort,
          full: formData.bioFull
        },
        location: {
          ...profile.location,
          city: formData.city,
          country: formData.country,
        },
        attributes: {
          ...profile.attributes,
          height: Number(formData.height) || undefined,
          weight: Number(formData.weight) || undefined,
          gender: formData.gender,
          sexualOrientation: formData.sexualOrientation,
          ethnicity: formData.ethnicity,
          nationality: formData.nationality,
          hairColor: formData.hairColor,
          eyeColor: formData.eyeColor,
          bodyType: formData.bodyType,
        }
      };

      onProfileUpdate(updatedProfile);

      toast({
        title: t('escort.profile.profile_updated'),
        description: t('escort.profile.profile_update_success'),
      });
    } catch (error) {
      console.error('Error saving profile information:', error);
      toast({
        title: t('escort.profile.update_error'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{t('escort.profile.basic_info')}</h2>
        <p className="text-muted-foreground">{t('escort.profile.basic_info_description')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('escort.profile.personal_details')}</CardTitle>
          <CardDescription>
            {t('escort.profile.personal_details_description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">{t('escort.profile.display_name')}</Label>
              <Input
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder={t('escort.profile.display_name_placeholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="age">{t('escort.profile.age')}</Label>
              <Input
                id="age"
                name="age"
                type="number"
                min={18}
                max={99}
                value={formData.age}
                onChange={handleNumberChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">{t('escort.profile.gender')}</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => handleSelectChange('gender', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('escort.profile.select_gender')} />
                </SelectTrigger>
                <SelectContent>
                  {genderOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sexualOrientation">
                {t('escort.profile.sexual_orientation')}
              </Label>
              <Select
                value={formData.sexualOrientation}
                onValueChange={(value) => handleSelectChange('sexualOrientation', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('escort.profile.select_orientation')} />
                </SelectTrigger>
                <SelectContent>
                  {orientationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('escort.profile.about_me')}</CardTitle>
          <CardDescription>
            {t('escort.profile.about_me_description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bioShort">{t('escort.profile.short_bio')}</Label>
            <Textarea
              id="bioShort"
              name="bioShort"
              value={formData.bioShort}
              onChange={handleInputChange}
              placeholder={t('escort.profile.short_bio_placeholder')}
              className="min-h-[80px]"
            />
            <p className="text-xs text-muted-foreground">
              {t('escort.profile.short_bio_hint')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bioFull">{t('escort.profile.full_bio')}</Label>
            <Textarea
              id="bioFull"
              name="bioFull"
              value={formData.bioFull}
              onChange={handleInputChange}
              placeholder={t('escort.profile.full_bio_placeholder')}
              className="min-h-[150px]"
            />
            <p className="text-xs text-muted-foreground">
              {t('escort.profile.full_bio_hint')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('escort.profile.location')}</CardTitle>
          <CardDescription>
            {t('escort.profile.location_description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('escort.profile.city')}</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={t('escort.profile.city_placeholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">{t('escort.profile.country')}</Label>
              <Input
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder={t('escort.profile.country_placeholder')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('escort.profile.physical_details')}</CardTitle>
          <CardDescription>
            {t('escort.profile.physical_details_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="height">{t('escort.profile.height')} (cm)</Label>
              <Input
                id="height"
                name="height"
                type="number"
                min={140}
                max={220}
                value={formData.height}
                onChange={handleNumberChange}
                placeholder="170"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight">{t('escort.profile.weight')} (kg)</Label>
              <Input
                id="weight"
                name="weight"
                type="number"
                min={40}
                max={150}
                value={formData.weight}
                onChange={handleNumberChange}
                placeholder="60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bodyType">{t('escort.profile.body_type')}</Label>
              <Select
                value={formData.bodyType}
                onValueChange={(value) => handleSelectChange('bodyType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('escort.profile.select_body_type')} />
                </SelectTrigger>
                <SelectContent>
                  {bodyTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hairColor">{t('escort.profile.hair_color')}</Label>
              <Input
                id="hairColor"
                name="hairColor"
                value={formData.hairColor}
                onChange={handleInputChange}
                placeholder={t('escort.profile.hair_color_placeholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eyeColor">{t('escort.profile.eye_color')}</Label>
              <Input
                id="eyeColor"
                name="eyeColor"
                value={formData.eyeColor}
                onChange={handleInputChange}
                placeholder={t('escort.profile.eye_color_placeholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ethnicity">{t('escort.profile.ethnicity')}</Label>
              <Select
                value={formData.ethnicity}
                onValueChange={(value) => handleSelectChange('ethnicity', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('escort.profile.select_ethnicity')} />
                </SelectTrigger>
                <SelectContent>
                  {ethnicityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nationality">{t('escort.profile.nationality')}</Label>
              <Input
                id="nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                placeholder={t('escort.profile.nationality_placeholder')}
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="border-t p-6">
          <Button
            className="ml-auto"
            onClick={saveProfileInfo}
            disabled={isSaving}
          >
            {isSaving ? t('escort.profile.saving') : t('escort.profile.save_changes')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default BasicInfoTab;
